import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, ShieldCheck, User as UserIcon } from "lucide-react";

function initials(name = "") {
  return name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

export default function Members() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
  }, []);

  const changeRole = async (id, role) => {
    try {
      await api.put(`/users/${id}/role`, { role });
      toast.success("Rol güncellendi");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Kullanıcı silindi");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm uppercase tracking-[0.3em] text-primary mb-2">Yönetim</p>
        <h1 className="font-anton text-4xl sm:text-5xl">ÜYELER</h1>
        <p className="text-muted-foreground mt-2">Ekip üyelerinin rollerini düzenleyin veya hesaplarını silin.</p>
      </div>

      <div className="space-y-2" data-testid="members-list">
        {users.map((u, i) => (
          <motion.div key={u.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
            <Card className="bg-card border-border p-4 flex items-center gap-4 hover:border-primary/40 transition-colors">
              <div className="w-11 h-11 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold shrink-0">
                {initials(u.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{u.name}</span>
                  {u.id === user?.id && <Badge variant="outline" className="border-primary/40 text-primary text-[11px]">Siz</Badge>}
                </div>
                <p className="text-sm text-muted-foreground truncate">{u.email}</p>
              </div>
              <Badge variant="outline" className={`shrink-0 ${u.role === "admin" ? "border-primary/40 text-primary" : "border-border text-muted-foreground"}`}>
                {u.role === "admin" ? <ShieldCheck className="w-3.5 h-3.5 mr-1" /> : <UserIcon className="w-3.5 h-3.5 mr-1" />}
                {u.role === "admin" ? "Yönetici" : "Üye"}
              </Badge>
              <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === user?.id}>
                <SelectTrigger data-testid={`role-select-${u.id}`} className="w-[130px] shrink-0"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Yönetici</SelectItem>
                  <SelectItem value="user">Üye</SelectItem>
                </SelectContent>
              </Select>
              {u.id !== user?.id && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" data-testid={`delete-user-${u.id}`} className="h-9 w-9 text-destructive hover:text-destructive shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-card border-border">
                    <AlertDialogHeader>
                      <AlertDialogTitle>Kullanıcıyı sil?</AlertDialogTitle>
                      <AlertDialogDescription>
                        <b>{u.name}</b> hesabı kalıcı olarak silinecek. Bu işlem geri alınamaz.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Vazgeç</AlertDialogCancel>
                      <AlertDialogAction data-testid={`confirm-delete-user-${u.id}`} onClick={() => remove(u.id)} className="bg-destructive hover:bg-destructive/90 text-white">Sil</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
